import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Row, Table, Spinner, Card, Badge, Container } from 'react-bootstrap';
import axios from 'axios';
import { BASE_URL } from '../../../services/Api';
import { authHeader } from '../../../services/auth-header';
import { FaArrowLeft } from 'react-icons/fa';

function AraiMView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [enquiryData, setEnquiryData] = useState(null);

    // Fetch enquiry data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`${BASE_URL}purchaseEnquiry/findById/${id}`, {
                    headers: authHeader()
                });
                setEnquiryData(response.data);
            } catch (error) {
                console.error("Error fetching enquiry:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [id]);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!enquiryData) {
        return <div className="alert alert-danger text-center mt-4">Enquiry not found</div>;
    }

    // Calculate totals
    const productsTotal = enquiryData.purchaseEnquiryItems?.reduce((sum, item) => sum + (item.totalinr || 0), 0) || 0;
    const chargesTotal = enquiryData.additionalCharges?.reduce((sum, charge) => sum + (charge.chargetotal || 0), 0) || 0;
    const grossTotal = productsTotal + chargesTotal;
    const roundedTotal = Math.round(grossTotal);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Draft': return 'secondary';
            case 'Submitted': return 'primary';
            case 'Approved': return 'success';
            case 'Rejected': return 'danger';
            default: return 'info';
        }
    };

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
        }).format(value || 0);

    return (
        <Container fluid className="p-3">
            {/* Header Section with Actions */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold text-primary mb-0">PURCHASE ENQUIRY - VIEW</h5>
                        <div className="d-flex gap-2">
                            <button
                                className="button-70 me-2"
                                onClick={() => navigate(`/araimduplicate/${enquiryData.id}`)}
                                title="Duplicate Entry"
                            >
                                📋 Duplicate
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                                <FaArrowLeft className="me-1" />
                                Back
                            </button>
                        </div>
                    </div>

                    <Row className="g-2">
                        <Col md={3}>
                            <div className="border-start border-3 border-primary ps-2">
                                <small className="text-muted">Enquiry No</small>
                                <div className="fw-bold">{enquiryData.enquiryno}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-success ps-2">
                                <small className="text-muted">Date</small>
                                <div className="fw-bold">
                                    {new Date(enquiryData.enquirydate).toLocaleDateString('en-IN')}
                                </div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-warning ps-2">
                                <small className="text-muted">Job Number</small>
                                <div className="fw-bold">{enquiryData.jobnumber}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-info ps-2">
                                <small className="text-muted">Status</small>
                                <div>
                                    <Badge
                                        bg={getStatusBadge(enquiryData.status)}
                                        className="px-2 py-1"
                                    >
                                        {enquiryData.status}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Supplier Information */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Supplier Details</h6>
                </Card.Header>
                <Card.Body className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Company Name</small>
                                <div className="fw-bold">{enquiryData.supplier?.companyname}</div>
                            </div>
                            <div>
                                <small className="text-muted">Contact Person</small>
                                <div className="fw-bold">{enquiryData.contactperson}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Phone</small>
                                <div className="fw-bold">{enquiryData.contactno}</div>
                            </div>
                            <div>
                                <small className="text-muted">Email</small>
                                <div className="fw-bold">{enquiryData.email}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div>
                                <small className="text-muted">Address</small>
                                <div className="fw-bold">
                                    {enquiryData.supplierMultipleAddress?.supplieraddress}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Product Details */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Product Details</h6>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table bordered size="sm" className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center fw-bold py-2">#</th>
                                    <th className="fw-bold py-2">Product</th>
                                    <th className="fw-bold py-2">Part No</th>
                                    <th className="fw-bold py-2">HSN Code</th>
                                    <th className="fw-bold py-2">Serial No</th>
                                    <th className="fw-bold py-2">MOC</th>
                                    <th className="fw-bold py-2">Type</th>
                                    <th className="fw-bold py-2">Size</th>
                                    <th className="fw-bold py-2">Rating</th>
                                    <th className="fw-bold py-2">Class</th>
                                    <th className="fw-bold py-2">Schedule</th>
                                    <th className="fw-bold py-2">Certification</th>
                                    <th className="fw-bold py-2 text-center">Qty</th>
                                    <th className="fw-bold py-2 text-center">UOM</th>
                                    <th className="fw-bold py-2 text-end">Unit Price</th>
                                    <th className="fw-bold py-2 text-end">Amount</th>
                                    <th className="fw-bold py-2 text-center">GST %</th>
                                    <th className="fw-bold py-2 text-end">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {enquiryData.purchaseEnquiryItems?.map((item, index) => (
                                    <tr key={index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>
                                            <div>{item.product.description}</div>
                                            {item.productremarks && (
                                                <div className="text-muted small">
                                                    <small>Remarks: {item.productremarks}</small>
                                                </div>
                                            )}
                                        </td>
                                        <td>{item.partno || "-"}</td>
                                        <td>{item.hsnsacno || "-"}</td>
                                        <td>{item.serialnumber || "-"}</td>
                                        <td>{item.moc || "-"}</td>
                                        <td>{item.type || "-"}</td>
                                        <td>{item.size || "-"}</td>
                                        <td>{item.rating || "-"}</td>
                                        <td>{item.classes || "-"}</td>
                                        <td>{item.schedule || "-"}</td>
                                        <td>{item.certification || "-"}</td>
                                        <td className="text-center">{item.quantity}</td>
                                        <td className="text-center">{item.uom}</td>
                                        <td className="text-end">{formatCurrency(item.unitrateinr)}</td>
                                        <td className="text-end">{formatCurrency(item.amount)}</td>
                                        <td className="text-center">{item.gst}%</td>
                                        <td className="text-end fw-bold">{formatCurrency(item.totalinr)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {/* Additional Charges */}
            {enquiryData.additionalCharges?.length > 0 && (
                <Card className="shadow-sm border-0 rounded mb-3">
                    <Card.Header className="bg-light py-2">
                        <h6 className="mb-0 fw-bold">Additional Charges</h6>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div className="table-responsive">
                            <Table bordered size="sm" className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="fw-bold py-2">#</th>
                                        <th className="fw-bold py-2">Charge Type</th>
                                        <th className="fw-bold py-2 text-end">Amount</th>
                                        <th className="fw-bold py-2 text-center">GST %</th>
                                        <th className="fw-bold py-2 text-end">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {enquiryData.additionalCharges.map((charge, index) => (
                                        <tr key={index}>
                                            <td className="text-center">{index + 1}</td>
                                            <td>{charge.chargetype}</td>
                                            <td className="text-end">{formatCurrency(charge.chargeamount)}</td>
                                            <td className="text-center">{charge.chargegst}%</td>
                                            <td className="text-end fw-bold">{formatCurrency(charge.chargetotal)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-light">
                                    <tr>
                                        <td colSpan="4" className="text-end fw-bold py-2">
                                            Charges Total:
                                        </td>
                                        <td className="text-end fw-bold py-2">
                                            {formatCurrency(chargesTotal)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Summary Section */}
            <Row>
                <Col lg={6} className="ms-auto">
                    <Card className="shadow-sm border-0 rounded">
                        <Card.Header className="bg-light py-2">
                            <h6 className="mb-0 fw-bold text-center">Purchase Enquiry Summary</h6>
                        </Card.Header>
                        <Card.Body className="p-3">
                            <div className="space-y-2">
                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">Products Total:</span>
                                    <span className="fw-bold">{formatCurrency(productsTotal)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">Additional Charges:</span>
                                    <span className="fw-bold">{formatCurrency(chargesTotal)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-2 border-top">
                                    <span className="fw-bold">Grand Total</span>
                                    <span className="fw-bold text-primary">{formatCurrency(grossTotal)}</span>
                                </div>

                                {roundedTotal !== grossTotal && (
                                    <div className="d-flex justify-content-between align-items-center py-1 bg-light rounded px-2">
                                        <span className="fw-semibold">Rounded Total:</span>
                                        <span className="fw-bold text-success">{formatCurrency(roundedTotal)}</span>
                                    </div>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Footer Actions */}
            <div className="text-center mt-3 pt-2 border-top">
                <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate(-1)}
                >
                    <FaArrowLeft className="me-1" />
                    Back to List
                </button>
            </div>
        </Container>
    );
}

export default AraiMView;